import React, { useState, useEffect, useRef } from 'react';
import { Settings, Power } from 'lucide-react';
import { ReactComponent as TitleLogo } from '../../assets/overwrite_title.svg';
import { GameSettings } from './GameSettings';
import { quitApplication } from '../../utils/electron';
import { useLanguage } from '../../hooks/useLanguage';

export const LoginScreen: React.FC<{ 
    onLogin: () => void;
    onBootComplete?: () => void;
    showSignIn?: boolean;
}> = ({ onLogin, onBootComplete, showSignIn = true }) => {
    const { t } = useLanguage();
    const [bootLines, setBootLines] = useState<string[]>([]);
    const [step, setStep] = useState<'CRT_STARTUP' | 'BOOT' | 'FADE_TO_LOGIN' | 'SIGNIN' | 'LOGIN' | 'FADE_TO_BOOT' | 'POST_LOGIN_BOOT'>(() => {
        // Si showSignIn est true, on démarre directement à SIGNIN (pas de boot)
        if (showSignIn) {
            return 'SIGNIN';
        }
        // Sinon, on démarre avec le boot complet
        return 'CRT_STARTUP';
    });
    const [isCrtStartupComplete, setIsCrtStartupComplete] = useState(showSignIn); // Déjà complété si showSignIn
    const [fadeOpacity, setFadeOpacity] = useState(0); // Pour les fondus progressifs
    const [buttonsVisible, setButtonsVisible] = useState(false); // Pour l'apparition des boutons
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [gender, setGender] = useState<'male' | 'female' | 'neutral'>('neutral');
    const [error, setError] = useState('');
    const [savedUser, setSavedUser] = useState<{ u: string, p: string } | null>(null);
    // États pour le post-it simplifié
    const [isPostitFalling, setIsPostitFalling] = useState(false);
    const [isPostitFlipped, setIsPostitFlipped] = useState(false);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);
    const [glitchIntensity, setGlitchIntensity] = useState(0);
    const [glitchTick, setGlitchTick] = useState(0);
    const [loginStatus, setLoginStatus] = useState<'IDLE' | 'SUCCESS' | 'ERROR'>('IDLE');

    // États pour la musique d'intro
    const [introMusic, setIntroMusic] = useState<HTMLAudioElement | null>(null);
    const [musicStarted, setMusicStarted] = useState(false);
    const [isFadingOut, setIsFadingOut] = useState(false);
    const [needsUserInteraction, setNeedsUserInteraction] = useState(false);
    const fadeIntervalRef = useRef<NodeJS.Timeout | null>(null);

    // État des paramètres du jeu
    const [gameSettings, setGameSettings] = useState(() => {
        const saved = localStorage.getItem('ALBION_GAME_SETTINGS');
        if (saved) {
            try {
                return JSON.parse(saved);
            } catch (e) { }
        }
        return {
            crtEffects: true,
            soundEnabled: true,
            soundVolume: 0.7,
            musicVolume: 0.7
        };
    });

    // Fonction pour faire un fondu de sortie de la musique
    const fadeOutMusic = () => {
        if (!introMusic || !musicStarted) return Promise.resolve();

        return new Promise<void>((resolve) => {
            setIsFadingOut(true);
            const originalVolume = introMusic.volume;
            const fadeStep = originalVolume / 50; // 50 étapes pour un fondu fluide

            fadeIntervalRef.current = setInterval(() => {
                if (introMusic.volume > fadeStep) {
                    introMusic.volume -= fadeStep;
                } else {
                    introMusic.volume = 0;
                    introMusic.pause();
                    if (fadeIntervalRef.current) {
                        clearInterval(fadeIntervalRef.current);
                    }
                    setIsFadingOut(false);
                    resolve();
                }
            }, 50); // Fondu sur ~2.5 secondes
        });
    };

    // Écouter les changements de paramètres et les demandes de fondu
    useEffect(() => {
        const handleSettingsChange = (e: CustomEvent) => {
            setGameSettings(e.detail);
        };

        const handleFadeOutMusic = () => {
            if (introMusic && musicStarted && !isFadingOut) {
                fadeOutMusic();
            }
        };

        window.addEventListener('GAME_SETTINGS_CHANGED', handleSettingsChange as EventListener);
        window.addEventListener('FADE_OUT_MUSIC', handleFadeOutMusic);

        return () => {
            window.removeEventListener('GAME_SETTINGS_CHANGED', handleSettingsChange as EventListener);
            window.removeEventListener('FADE_OUT_MUSIC', handleFadeOutMusic);
        };
    }, [introMusic, musicStarted, isFadingOut]);

    // Initialiser la musique d'intro
    useEffect(() => {
        if (gameSettings.soundEnabled && !introMusic) {
            try {
                // Dans Electron, les fichiers doivent être dans le dossier public
                const audio = new Audio('./data/musique/The Passenger.mp3');
                audio.loop = true;
                // Volume final = volume général × volume musique
                const finalVolume = (gameSettings.soundVolume ?? 0.7) * (gameSettings.musicVolume ?? 0.7);
                audio.volume = finalVolume;

                // Gérer les erreurs de chargement
                audio.addEventListener('error', (e) => {
                    console.warn('Erreur de chargement audio:', e);
                    console.warn('Fichier recherché: ./data/musique/The Passenger.mp3');
                    console.warn('Vérifiez que le fichier existe et est accessible');
                });

                audio.addEventListener('canplaythrough', () => {
                    console.log('Musique d\'intro chargée avec succès depuis ./data/musique/The Passenger.mp3');
                });

                audio.addEventListener('loadstart', () => {
                    console.log('Début du chargement de la musique d\'intro...');
                });

                // Précharger le fichier
                audio.load();

                setIntroMusic(audio);
            } catch (e) {
                console.warn('Impossible de créer l\'objet Audio:', e);
            }
        }
    }, [gameSettings.soundEnabled, introMusic]); // Retirer soundVolume et musicVolume des dépendances

    // Mettre à jour le volume de la musique en temps réel
    useEffect(() => {
        if (introMusic && gameSettings.soundEnabled) {
            // Volume final = volume général × volume musique
            const finalVolume = (gameSettings.soundVolume ?? 0.7) * (gameSettings.musicVolume ?? 0.7);
            introMusic.volume = finalVolume;
            console.log(`Volume musique mis à jour: ${Math.round(gameSettings.soundVolume * 100)}% × ${Math.round(gameSettings.musicVolume * 100)}% = ${Math.round(finalVolume * 100)}%`);
        }
    }, [introMusic, gameSettings.soundVolume, gameSettings.musicVolume, gameSettings.soundEnabled]);

    // Gérer la pause/reprise de la musique quand soundEnabled change
    useEffect(() => {
        if (introMusic && musicStarted) {
            if (gameSettings.soundEnabled) {
                // Reprendre la musique si elle était en pause
                if (introMusic.paused) {
                    console.log('Reprise de la musique (son réactivé)');
                    introMusic.play().catch(e => console.warn('Erreur reprise musique:', e));
                }
            } else {
                // Mettre en pause la musique
                if (!introMusic.paused) {
                    console.log('Pause de la musique (son désactivé)');
                    introMusic.pause();
                }
            }
        }
    }, [introMusic, musicStarted, gameSettings.soundEnabled]);

    // Démarrer la musique après 0.5 seconde sur les écrans LOGIN/SIGNIN
    useEffect(() => {
        if ((step === 'LOGIN' || step === 'SIGNIN') && introMusic && gameSettings.soundEnabled && !musicStarted) {
            const timer = setTimeout(() => {
                console.log('Tentative de démarrage de la musique d\'intro...');
                introMusic.play().then(() => {
                    console.log('Musique d\'intro démarrée avec succès');
                    setMusicStarted(true);
                    setNeedsUserInteraction(false);
                }).catch(e => {
                    console.warn('Impossible de jouer la musique d\'intro:', e);
                    console.warn('Cela peut être dû aux restrictions du navigateur - interaction utilisateur requise');
                    setNeedsUserInteraction(true);
                });
            }, 500); // Réduit de 2000ms à 500ms (0.5 seconde)

            return () => clearTimeout(timer);
        }
    }, [step, introMusic, gameSettings.soundEnabled, musicStarted]);

    // Fonction pour démarrer la musique lors d'une interaction utilisateur
    const handleUserInteraction = () => {
        if (needsUserInteraction && introMusic && gameSettings.soundEnabled && !musicStarted) {
            introMusic.play().then(() => {
                console.log('Musique d\'intro démarrée après interaction utilisateur');
                setMusicStarted(true);
                setNeedsUserInteraction(false);
            }).catch(e => {
                console.warn('Échec du démarrage de la musique même après interaction:', e);
            });
        }
    };

    // Nettoyer les ressources audio
    useEffect(() => {
        return () => {
            if (fadeIntervalRef.current) {
                clearInterval(fadeIntervalRef.current);
            }
            if (introMusic) {
                introMusic.pause();
                introMusic.src = '';
            }
        };
    }, [introMusic]);

    // Animation de glitch continue quand glitchIntensity > 0
    useEffect(() => {
        if (glitchIntensity > 0) {
            const interval = setInterval(() => {
                setGlitchTick(prev => prev + 1);
            }, 50); // Mise à jour toutes les 50ms pour un glitch fluide
            return () => clearInterval(interval);
        }
    }, [glitchIntensity]);

    // Persistence: Load state
    useEffect(() => {
        const saved = localStorage.getItem('ALBION_SAVE_DATA');
        if (saved) {
            try {
                const data = JSON.parse(saved);
                if (data.isPostitFalling) setIsPostitFalling(data.isPostitFalling);
                if (data.isPostitFlipped) setIsPostitFlipped(data.isPostitFlipped);
            } catch (e) { }
        }
    }, []);

    // Vérifier s'il y a un utilisateur sauvegardé au démarrage (quand on démarre directement à SIGNIN)
    useEffect(() => {
        if (showSignIn && step === 'SIGNIN') {
            // Faire apparaître les boutons immédiatement si on démarre directement sur SIGNIN
            setTimeout(() => setButtonsVisible(true), 300);
            
            const saved = localStorage.getItem('ALBION_SAVE_DATA');
            if (saved) {
                try {
                    const data = JSON.parse(saved);
                    if (data.user) {
                        setSavedUser(data.user);
                        setStep('LOGIN');
                        return;
                    }
                } catch (e) { }
            }
            // Legacy fallback
            const stored = localStorage.getItem('albion_user');
            if (stored) {
                setSavedUser(JSON.parse(stored));
                setStep('LOGIN');
            }
        }
    }, [showSignIn, step]);

    // Synthesis Helper for diegetic sounds
    const playBootSound = () => {
        if (!gameSettings.soundEnabled) return;

        try {
            const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();

            // Son de bip système plus authentique (comme un PC des années 90)
            osc.type = 'square';
            osc.frequency.setValueAtTime(800, ctx.currentTime);

            gain.gain.setValueAtTime(0.03 * gameSettings.soundVolume, ctx.currentTime);
            gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.08);

            osc.connect(gain);
            gain.connect(ctx.destination);

            osc.start();
            osc.stop(ctx.currentTime + 0.08);
        } catch (e) {
            // Ignore if audio context fails
        }
    };

    const playSuccessSound = () => {
        if (!gameSettings.soundEnabled) return;

        try {
            const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
            const now = ctx.currentTime;
            const volume = 0.12 * gameSettings.soundVolume;

            // Son 1 : Double bip court et précis (style validation système)
            const osc1 = ctx.createOscillator();
            const gain1 = ctx.createGain();
            osc1.type = 'sine';
            osc1.frequency.setValueAtTime(1200, now);
            gain1.gain.setValueAtTime(volume * 0.8, now);
            gain1.gain.exponentialRampToValueAtTime(0.001, now + 0.08);
            osc1.connect(gain1);
            gain1.connect(ctx.destination);
            osc1.start(now);
            osc1.stop(now + 0.08);

            // Son 2 : Deuxième bip identique légèrement décalé
            const osc2 = ctx.createOscillator();
            const gain2 = ctx.createGain();
            osc2.type = 'sine';
            osc2.frequency.setValueAtTime(1200, now + 0.12);
            gain2.gain.setValueAtTime(volume * 0.8, now + 0.12);
            gain2.gain.exponentialRampToValueAtTime(0.001, now + 0.20);
            osc2.connect(gain2);
            gain2.connect(ctx.destination);
            osc2.start(now + 0.12);
            osc2.stop(now + 0.20);

            // Son 3 : Tonalité de confirmation plus grave et soutenue
            const osc3 = ctx.createOscillator();
            const gain3 = ctx.createGain();
            osc3.type = 'square';
            osc3.frequency.setValueAtTime(600, now + 0.25);
            gain3.gain.setValueAtTime(0, now + 0.25);
            gain3.gain.linearRampToValueAtTime(volume * 0.5, now + 0.27);
            gain3.gain.exponentialRampToValueAtTime(0.001, now + 0.50);
            osc3.connect(gain3);
            gain3.connect(ctx.destination);
            osc3.start(now + 0.25);
            osc3.stop(now + 0.50);

        } catch (e) {
            // Ignore if audio context fails
        }
    };


    const lines = [
        "BIOS Version 4.02 - MIRROR CELL INC.",
        "CPU: i486DX @ 33MHz... OK",
        "MEMORY: 16384 KB... OK",
        "LOADING MICRO-KERNEL...",
        "NET_LINK: ESTABLISHING CRYPTED TUNNEL...",
        "IDENT_V: UNIT_62_ALBION DETECTED.",
        "SYSTEM PRE-READY."
    ];

    const postLoginLines = [
        t('loginScreen.authSuccess'),
        t('loginScreen.loadingProfile'),
        t('loginScreen.initSecurity'),
        t('loginScreen.connectingNetwork'),
        t('loginScreen.loadingInterface'),
        t('loginScreen.systemReady')
    ];

    useEffect(() => {
        if (step === 'CRT_STARTUP') {
            const timer = setTimeout(() => {
                setStep('BOOT');
                setIsCrtStartupComplete(true);
            }, 800); // Durée de l'animation CRT
            return () => clearTimeout(timer);
        } else if (step === 'BOOT') {
            let currentLine = 0;
            const interval = setInterval(() => {
                if (currentLine < lines.length) {
                    setBootLines(prev => [...prev, lines[currentLine]]);
                    // Son seulement au début et à la fin
                    if (currentLine === 0 || currentLine === lines.length - 1) {
                        playBootSound();
                    }
                    currentLine++;
                } else {
                    clearInterval(interval);
                    setTimeout(() => {
                        // Si showSignIn est false, on s'arrête ici et on notifie App.tsx
                        if (!showSignIn && onBootComplete) {
                            onBootComplete();
                            return;
                        }
                        
                        // Sinon, passer à l'état de fondu
                        setStep('FADE_TO_LOGIN');
                    }, 1000);
                }
            }, 300);
            return () => clearInterval(interval);
        } else if (step === 'FADE_TO_LOGIN') {
            // Animation de fondu progressif vers l'écran de login
            let opacity = 0;
            const fadeInterval = setInterval(() => {
                opacity += 0.05;
                setFadeOpacity(opacity);
                if (opacity >= 1) {
                    clearInterval(fadeInterval);
                    // Déterminer si on va vers SIGNIN ou LOGIN
                    const saved = localStorage.getItem('ALBION_SAVE_DATA');
                    if (saved) {
                        try {
                            const data = JSON.parse(saved);
                            if (data.user) {
                                setSavedUser(data.user);
                                setStep('LOGIN');
                                // Faire apparaître les boutons progressivement
                                setTimeout(() => setButtonsVisible(true), 300);
                                return;
                            }
                        } catch (e) { }
                    }
                    // Legacy fallback
                    const stored = localStorage.getItem('albion_user');
                    if (stored) {
                        setSavedUser(JSON.parse(stored));
                        setStep('LOGIN');
                    } else {
                        setStep('SIGNIN');
                    }
                    // Faire apparaître les boutons progressivement
                    setTimeout(() => setButtonsVisible(true), 300);
                }
            }, 30); // Fondu sur ~600ms
            return () => clearInterval(fadeInterval);
        } else if (step === 'FADE_TO_BOOT') {
            // Animation de fondu inverse avant les logs
            let opacity = 1;
            const fadeInterval = setInterval(() => {
                opacity -= 0.05;
                setFadeOpacity(opacity);
                if (opacity <= 0) {
                    clearInterval(fadeInterval);
                    setStep('POST_LOGIN_BOOT');
                }
            }, 30); // Fondu sur ~600ms
            return () => clearInterval(fadeInterval);
        } else if (step === 'POST_LOGIN_BOOT') {
            // Séquence de boot après login
            setBootLines([]); // Reset des lignes
            setFadeOpacity(0); // Reset de l'opacité
            let currentLine = 0;
            const interval = setInterval(() => {
                if (currentLine < postLoginLines.length) {
                    setBootLines(prev => [...prev, postLoginLines[currentLine]]);
                    if (currentLine === 0 || currentLine === postLoginLines.length - 1) {
                        playBootSound();
                    }
                    currentLine++;
                } else {
                    clearInterval(interval);
                    // Attendre que la musique soit complètement coupée avant de continuer
                    setTimeout(() => {
                        onLogin();
                    }, 1000);
                }
            }, 400);
            return () => clearInterval(interval);
        }
    }, [step]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (step === 'SIGNIN') {
            if (username.length < 3 || password.length < 4) {
                setError(t('loginScreen.errorShortCredentials'));
                return;
            }
            const newUser = { u: username, p: password, gender: gender };
            const saved = localStorage.getItem('ALBION_SAVE_DATA');
            let data = saved ? JSON.parse(saved) : {};
            data.user = newUser;
            localStorage.setItem('ALBION_SAVE_DATA', JSON.stringify(data));
            // Keep legacy for safety
            localStorage.setItem('albion_user', JSON.stringify(newUser));

            setSavedUser(newUser);
            setUsername('');
            setPassword('');
            setGender('neutral');
            setStep('LOGIN');
        } else {
            console.log('Login attempt:', username, password);
            console.log('Saved user:', savedUser);

            if (savedUser && username === savedUser.u && password === savedUser.p) {
                // SUCCESS
                setLoginStatus('SUCCESS');
                playSuccessSound(); // Son de connexion réussie

                // Petite pause pour laisser le joueur voir "ACCESS GRANTED"
                setTimeout(async () => {
                    setIsTransitioning(true);

                    console.log('LoginScreen - Login successful, emitting USER_SESSION_CHANGED');
                    console.log('LoginScreen - Current localStorage:', localStorage.getItem('ALBION_SAVE_DATA'));

                    // Émettre l'événement de changement de session utilisateur
                    window.dispatchEvent(new CustomEvent('USER_SESSION_CHANGED'));

                    // Commencer le fondu de sortie de la musique
                    await fadeOutMusic();

                    // Passer à l'état de fondu inverse
                    setStep('FADE_TO_BOOT');
                }, 1500);
            } else {
                // ERROR
                setLoginStatus('ERROR');
                setError(t('loginScreen.authFailed'));

                // Reset to IDLE after 2 seconds
                setTimeout(() => {
                    if (loginStatus === 'ERROR') {
                        // setLoginStatus('IDLE'); // On laisse l'utilisateur reset en tapant
                    }
                }, 2000);
            }
        }
    };

    return (
        <div className={`fixed inset-0 z-[10000] flex flex-col items-center justify-center font-mono text-cyan-400 overflow-hidden transition-all duration-75 ${step === 'BOOT' || step === 'POST_LOGIN_BOOT' || step === 'CRT_STARTUP' || step === 'FADE_TO_BOOT' ? 'bg-black' : 'bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900'
            }`}
            style={{
                filter: glitchIntensity > 0.5 ? `brightness(${1 + glitchIntensity * 0.3}) contrast(${1 + glitchIntensity * 0.4}) saturate(${1 + glitchIntensity * 0.3})` : 'none',
                transform: glitchIntensity > 0.8 ? `translate(${Math.random() * 6 - 3}px, ${Math.random() * 6 - 3}px)` : 'none',
                opacity: step === 'FADE_TO_LOGIN' ? fadeOpacity : step === 'FADE_TO_BOOT' ? fadeOpacity : 1
            }}>
            {/* Effet CRT au démarrage */}
            {step === 'CRT_STARTUP' && (
                <div className="absolute inset-0 animate-crt-startup z-[20000]"></div>
            )}
            {/* Boutons en haut à droite - Apparition progressive */}
            {(step === 'SIGNIN' || step === 'LOGIN') && !isTransitioning && (
                <div className={`fixed top-4 right-4 z-[10001] flex gap-2 transition-all duration-1000 ${
                    buttonsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'
                }`}>
                    <button
                        onClick={() => setIsSettingsOpen(true)}
                        className="p-3 bg-slate-700 text-slate-300 border border-slate-600 hover:bg-slate-600 transition-colors shadow-lg"
                        title={t('loginScreen.systemSettings')}
                    >
                        <Settings size={20} />
                    </button>
                    <button
                        onClick={quitApplication}
                        className="p-3 bg-red-800 text-red-300 border border-red-600 hover:bg-red-700 transition-colors shadow-lg"
                        title={t('loginScreen.quitApplication')}
                    >
                        <Power size={20} />
                    </button>
                </div>
            )}
            {/* Fond d'écran high-tech pour login/signin */}
            {(step === 'SIGNIN' || step === 'LOGIN') && (
                <div className="absolute inset-0 z-0">
                    {/* Grille hexagonale */}
                    <div className="absolute inset-0 opacity-10">
                        <svg width="100%" height="100%" className="absolute inset-0">
                            <defs>
                                <pattern id="hexGrid" x="0" y="0" width="60" height="52" patternUnits="userSpaceOnUse">
                                    <polygon points="30,2 50,15 50,37 30,50 10,37 10,15" fill="none" stroke="currentColor" strokeWidth="0.5" opacity="0.3" />
                                </pattern>
                            </defs>
                            <rect width="100%" height="100%" fill="url(#hexGrid)" className="text-slate-600" />
                        </svg>
                    </div>

                    {/* Lignes de données qui défilent */}
                    <div className="absolute inset-0 overflow-hidden opacity-20">
                        {[...Array(8)].map((_, i) => (
                            <div
                                key={i}
                                className="absolute h-px bg-gradient-to-r from-transparent via-cyan-500 to-transparent animate-pulse"
                                style={{
                                    top: `${12 + i * 12}%`,
                                    left: '-100%',
                                    width: '200%',
                                    animationDelay: `${i * 0.8}s`,
                                    animationDuration: '4s'
                                }}
                            />
                        ))}
                    </div>

                    {/* Texte de statut système */}
                    <div className="absolute top-4 left-4 text-[8px] text-slate-600 font-mono opacity-40 space-y-1">
                        <div>{t('loginScreen.systemStatus')}: {t('loginScreen.operational')}</div>
                        <div>{t('loginScreen.networkLink')}: {t('loginScreen.encrypted')}</div>
                        <div>{t('loginScreen.securityLevel')}: {t('loginScreen.maximum')}</div>
                        <div>PROC_{t('loginScreen.load')}: 23%</div>
                    </div>

                    {/* Indicateur de charge en bas à droite */}
                    <div className="absolute bottom-4 right-4 text-[8px] text-slate-600 font-mono opacity-40">
                        <div className="flex items-center gap-2">
                            <span>{t('loginScreen.load')}</span>
                            <div className="w-16 h-1 bg-slate-800 overflow-hidden">
                                <div className="h-full bg-cyan-500 animate-pulse" style={{ width: '23%' }}></div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {step === 'BOOT' || step === 'POST_LOGIN_BOOT' ? (
                <div className="w-full max-w-lg space-y-1 text-center">
                    {bootLines.map((line, i) => (
                        <div key={i} className="text-cyan-400 uppercase">{line}</div>
                    ))}
                    <div className="animate-pulse">_</div>

                    {/* Indicateur de fondu musical pendant POST_LOGIN_BOOT */}
                    {step === 'POST_LOGIN_BOOT' && isFadingOut && (
                        <div className="mt-4 text-xs text-slate-500 animate-pulse">
                            {t('loginScreen.stoppingAudio')}
                        </div>
                    )}
                </div>
            ) : step === 'SIGNIN' ? (
                <div className={`w-full max-w-sm flex flex-col items-center relative transition-all duration-1000 ${
                    buttonsVisible ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-95 translate-y-8'
                }`}
                    style={{ transitionDelay: buttonsVisible ? '400ms' : '0ms' }}
                >
                    <div className="w-full border border-slate-600 p-8 shadow-2xl shadow-black/50 bg-slate-900/90 z-10"
                        onClick={handleUserInteraction}
                    >
                        <div className="text-center mb-8 border-b border-slate-600 pb-4">
                            <h2 className="text-xl font-black tracking-widest text-cyan-400">{t('loginScreen.createProfile')}</h2>
                            <p className="text-[10px] text-slate-400 mt-1 uppercase">{t('loginScreen.dst')}</p>
                            {/* Indicateur si interaction requise pour la musique */}
                            {needsUserInteraction && (
                                <p className="text-[8px] text-yellow-500 mt-2 animate-pulse">
                                    {t('loginScreen.clickToActivateAudio')}
                                </p>
                            )}
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-6" autoComplete="off">
                            <div>
                                <label className="block text-[10px] mb-2 text-slate-400 uppercase tracking-widest">{t('loginScreen.newUsername')}</label>
                                <input
                                    autoFocus
                                    type="text"
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value.toUpperCase())}
                                    className="w-full bg-slate-800 border border-slate-600 p-2 text-cyan-300 outline-none focus:border-cyan-500 transition-all font-mono"
                                    placeholder={t('loginScreen.usernamePlaceholder')}
                                    autoComplete="off"
                                    autoCorrect="off"
                                    autoCapitalize="off"
                                    spellCheck="false"
                                />
                            </div>
                            <div>
                                <label className="block text-[10px] mb-2 text-slate-400 uppercase tracking-widest">{t('loginScreen.newPassword')}</label>
                                <input
                                    type="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="w-full bg-slate-800 border border-slate-600 p-2 text-cyan-300 outline-none focus:border-cyan-500 transition-all font-mono"
                                    placeholder={t('loginScreen.passwordPlaceholder')}
                                    autoComplete="off"
                                    autoCorrect="off"
                                    autoCapitalize="off"
                                    spellCheck="false"
                                />
                            </div>
                            <div>
                                <label className="block text-[10px] mb-2 text-slate-400 uppercase tracking-widest">Photo de badge</label>
                                <div className="grid grid-cols-3 gap-3">
                                    <button
                                        type="button"
                                        onClick={() => setGender('male')}
                                        className={`p-3 border-2 transition-all ${
                                            gender === 'male' 
                                                ? 'border-cyan-400 bg-cyan-900/30' 
                                                : 'border-slate-600 bg-slate-800 hover:border-slate-500'
                                        }`}
                                    >
                                        <div className="text-[8px] text-slate-400 mb-1">Homme</div>
                                        <div className="w-full aspect-square bg-slate-700 rounded flex items-center justify-center">
                                            <span className="text-2xl">👨</span>
                                        </div>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setGender('female')}
                                        className={`p-3 border-2 transition-all ${
                                            gender === 'female' 
                                                ? 'border-cyan-400 bg-cyan-900/30' 
                                                : 'border-slate-600 bg-slate-800 hover:border-slate-500'
                                        }`}
                                    >
                                        <div className="text-[8px] text-slate-400 mb-1">Femme</div>
                                        <div className="w-full aspect-square bg-slate-700 rounded flex items-center justify-center">
                                            <span className="text-2xl">👩</span>
                                        </div>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setGender('neutral')}
                                        className={`p-3 border-2 transition-all ${
                                            gender === 'neutral' 
                                                ? 'border-cyan-400 bg-cyan-900/30' 
                                                : 'border-slate-600 bg-slate-800 hover:border-slate-500'
                                        }`}
                                    >
                                        <div className="text-[8px] text-slate-400 mb-1">Neutre</div>
                                        <div className="w-full aspect-square bg-slate-700 rounded flex items-center justify-center">
                                            <span className="text-2xl">👤</span>
                                        </div>
                                    </button>
                                </div>
                            </div>
                            {error && <div className="text-[10px] text-red-400 font-bold bg-red-900/20 p-2 border border-red-800/50">{error}</div>}
                            <button
                                type="submit"
                                className="w-full bg-slate-700 text-cyan-400 font-bold py-3 hover:bg-slate-600 transition-all transform active:scale-95 shadow-lg border border-slate-600"
                            >
                                {t('loginScreen.registerProfile')}
                            </button>
                        </form>
                    </div>
                </div>
            ) : step === 'LOGIN' ? (
                <div className="w-full max-w-sm flex flex-col items-center relative mt-20">
                    {/* Dynamic Post-it avec image - HIGH Z-INDEX TO BYPASS CRT - Apparition progressive */}
                    {!isTransitioning && (
                        <div
                            className={`fixed shadow-2xl scale-110 group cursor-pointer select-none pointer-events-auto z-[20000]
                                ${buttonsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-8'}
                            `}
                            style={{
                                left: 40,
                                top: 40,
                                width: 'auto',
                                height: 'auto',
                                transitionDelay: buttonsVisible && !isPostitFalling ? '500ms' : '0ms',
                                transformStyle: 'preserve-3d',
                                perspective: '1000px',
                                // Animation physique réaliste avec gravité et rebond
                                transition: isPostitFalling 
                                    ? 'transform 2.5s cubic-bezier(0.25, 0.46, 0.45, 0.94), opacity 0.3s ease-out' 
                                    : 'transform 2s cubic-bezier(0.175, 0.885, 0.32, 1.275), opacity 1s ease-in-out',
                                transform: `
                                    ${isPostitFalling 
                                        ? 'translateY(calc(100vh - 100px)) translateX(50px) rotate(35deg) scale(0.8)' 
                                        : 'translateY(0) translateX(0) rotate(-4deg) scale(1)'
                                    } 
                                    ${isPostitFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)'}
                                `,
                                // Ajouter une ombre qui suit le mouvement
                                filter: isPostitFalling 
                                    ? 'drop-shadow(8px 12px 20px rgba(0,0,0,0.4))' 
                                    : 'drop-shadow(2px 4px 8px rgba(0,0,0,0.2))'
                            }}
                            onClick={(e) => {
                                e.stopPropagation();
                                if (isPostitFalling) {
                                    // Remettre en place avec animation fluide et rebond
                                    setIsPostitFalling(false);
                                    // Remettre le post-it à l'endroit immédiatement quand on le remet en place
                                    setIsPostitFlipped(false);
                                    const saved = localStorage.getItem('ALBION_SAVE_DATA');
                                    let data = saved ? JSON.parse(saved) : {};
                                    data.isPostitFalling = false;
                                    data.isPostitFlipped = false;
                                    localStorage.setItem('ALBION_SAVE_DATA', JSON.stringify(data));
                                } else {
                                    // Faire tomber avec animation et retournement synchronisé
                                    setIsPostitFalling(true);
                                    // Retourner le post-it immédiatement pour que le texte disparaisse pendant la chute
                                    setIsPostitFlipped(true);
                                    const saved = localStorage.getItem('ALBION_SAVE_DATA');
                                    let data = saved ? JSON.parse(saved) : {};
                                    data.isPostitFalling = true;
                                    data.isPostitFlipped = true;
                                    localStorage.setItem('ALBION_SAVE_DATA', JSON.stringify(data));
                                }
                            }}
                            onMouseEnter={(e) => {
                                if (!isPostitFalling) {
                                    e.currentTarget.style.transform = 'translateY(-4px) rotate(-2deg) scale(1.02)';
                                }
                            }}
                            onMouseLeave={(e) => {
                                if (!isPostitFalling) {
                                    e.currentTarget.style.transform = 'translateY(0) rotate(-4deg) scale(1)';
                                }
                            }}
                        >
                            {/* Afficher la face avant avec l'image seulement si pas retourné */}
                            <div className={`relative w-64 h-64 overflow-hidden transition-opacity duration-300 ${
                                isPostitFlipped ? 'opacity-0' : 'opacity-100'
                            }`}>
                                {/* Image du post-it recadrée pour enlever les zones transparentes */}
                                <img
                                    src={`${process.env.PUBLIC_URL}/data/png/postit.png`}
                                    alt="Post-it"
                                    className="w-full h-full object-cover"
                                    style={{
                                        objectPosition: '50% 45%', // Ajuster le centrage
                                        transform: 'scale(1.8)', // Agrandir davantage pour éliminer les bords
                                        clipPath: 'inset(10% 10% 10% 10%)' // Découper les bords transparents
                                    }}
                                />

                                {/* Contenu texte superposé au centre - disparaît avec le retournement */}
                                <div className={`absolute inset-0 flex flex-col justify-between p-6 transition-all duration-200 ${
                                    isPostitFlipped ? 'opacity-0 scale-95' : 'opacity-100 scale-100'
                                }`}>
                                    {/* En-tête en haut */}
                                    <div className="text-center">
                                        <div className="border-b border-black/10 pb-1 text-[10px] font-black text-black/40 uppercase tracking-tighter">{t('loginScreen.internalNote')}</div>
                                    </div>

                                    {/* Identifiants au centre */}
                                    <div className="text-center space-y-3 -mt-4">
                                        <div className="text-blue-800 font-handwriting text-lg leading-tight">
                                            <span className="text-[10px] opacity-40 block mb-1 uppercase">{t('loginScreen.ident')}</span>
                                            <span className="font-bold tracking-widest">{savedUser?.u}</span>
                                        </div>
                                        <div className="text-blue-800 font-handwriting text-lg leading-tight">
                                            <span className="text-[10px] opacity-40 block mb-1 uppercase">{t('loginScreen.pass')}</span>
                                            <span className="font-bold tracking-widest">{savedUser?.p}</span>
                                        </div>
                                    </div>

                                    {/* Pied de page en bas */}
                                    <div className="text-center">
                                        <div className="text-[8px] text-red-600 font-bold italic opacity-30 uppercase tracking-tighter">{t('loginScreen.confidential')}</div>
                                    </div>
                                </div>
                            </div>

                            {/* Afficher la face arrière (verso de l'image) seulement si retourné */}
                            <div className={`absolute inset-0 w-64 h-64 overflow-hidden transition-opacity duration-300 ${
                                isPostitFlipped ? 'opacity-100' : 'opacity-0'
                            }`}>
                                {/* Image du post-it retournée et recadrée */}
                                <img
                                    src={`${process.env.PUBLIC_URL}/data/png/postit.png`}
                                    alt="Post-it verso"
                                    className="w-full h-full object-cover"
                                    style={{
                                        objectPosition: '50% 45%', // Ajuster le centrage
                                        transform: 'scale(1.8) scaleX(-1)', // Agrandir davantage et retourner
                                        filter: 'brightness(0.9) contrast(0.8)', // Légèrement plus sombre
                                        clipPath: 'inset(10% 10% 10% 10%)' // Découper les bords transparents
                                    }}
                                />

                                {/* Verso vierge - quelques éléments très subtiles */}
                                <div className="absolute inset-0 flex flex-col justify-center items-center opacity-10">
                                    <div className="w-3/4 h-px bg-black/20 mb-6"></div>
                                    <div className="w-3/4 h-px bg-black/20 mb-6"></div>
                                    <div className="w-3/4 h-px bg-black/20 mb-6"></div>
                                    <div className="w-3/4 h-px bg-black/20"></div>
                                </div>

                                {/* Petit marquage au verso */}
                                <div className="absolute bottom-4 left-4 text-[6px] text-black/20 uppercase tracking-wider transform scale-x-[-1]">
                                    DST - 1996
                                </div>
                            </div>
                        </div>
                    )}

                    {isTransitioning && (
                        <div className="fixed inset-0 z-[1000] bg-black flex flex-col items-center justify-center animate-in fade-in duration-500">
                            <div className="w-64 h-1 bg-slate-800 mb-4 overflow-hidden relative">
                                <div className="absolute inset-0 bg-cyan-400 animate-loading-bar"></div>
                            </div>
                            <p className="text-xs tracking-[0.5em] text-cyan-400 animate-pulse">{t('loginScreen.loadingSession')}</p>
                        </div>
                    )}

                    {/* Game Title - High and Large - Animation cinématographique */}
                    <div className={`mb-12 z-20 w-[180%] flex justify-center pointer-events-auto -mt-48 transition-all duration-1000 ${
                        buttonsVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-90'
                    }`}
                        onMouseEnter={() => setGlitchIntensity(1)}
                        onMouseLeave={() => setGlitchIntensity(0)}
                        style={{
                            opacity: isTransitioning ? 0 : (buttonsVisible ? 1 : 0),
                            transitionDelay: buttonsVisible ? '200ms' : '0ms',
                            perspective: '1000px',
                            filter: glitchIntensity > 0 ? `drop-shadow(0 0 ${25 * glitchIntensity}px rgba(6,182,212,0.9)) drop-shadow(0 0 ${15 * glitchIntensity}px rgba(59,130,246,0.6)) brightness(${1 + glitchIntensity * 0.25})` : 'none'
                        }}>
                        
                        {/* Effet de scan laser horizontal qui révèle le titre */}
                        {buttonsVisible && (
                            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                                <div 
                                    className="absolute w-full h-1 bg-gradient-to-r from-transparent via-cyan-400 to-transparent opacity-80 blur-sm"
                                    style={{
                                        animation: 'scanReveal 2s ease-out forwards',
                                        boxShadow: '0 0 20px rgba(6,182,212,0.8), 0 0 40px rgba(6,182,212,0.4)'
                                    }}
                                />
                            </div>
                        )}

                        {/* Particules lumineuses qui convergent vers le titre */}
                        {buttonsVisible && (
                            <>
                                {[...Array(12)].map((_, i) => (
                                    <div
                                        key={i}
                                        className="absolute w-1 h-1 bg-cyan-400 rounded-full"
                                        style={{
                                            left: `${Math.random() * 100}%`,
                                            top: `${Math.random() * 100}%`,
                                            animationName: 'particleConverge',
                                            animationDuration: `${1.5 + Math.random() * 0.5}s`,
                                            animationTimingFunction: 'ease-out',
                                            animationFillMode: 'forwards',
                                            animationDelay: `${i * 0.08}s`,
                                            boxShadow: '0 0 8px rgba(6,182,212,0.8)',
                                            opacity: 0
                                        }}
                                    />
                                ))}
                            </>
                        )}

                        {/* Effet de grille qui se matérialise */}
                        {buttonsVisible && (
                            <div 
                                className="absolute inset-0 pointer-events-none"
                                style={{
                                    backgroundImage: 'linear-gradient(0deg, transparent 48%, rgba(6,182,212,0.1) 49%, rgba(6,182,212,0.1) 51%, transparent 52%), linear-gradient(90deg, transparent 48%, rgba(6,182,212,0.1) 49%, rgba(6,182,212,0.1) 51%, transparent 52%)',
                                    backgroundSize: '30px 30px',
                                    animation: 'gridMaterialize 1.5s ease-out forwards',
                                    opacity: 0
                                }}
                            />
                        )}

                        {/* Le titre avec effet de glitch initial */}
                        <div
                            className="relative"
                            style={{
                                animation: buttonsVisible ? 'titleGlitchIn 1.2s ease-out forwards' : 'none'
                            }}
                        >
                            <TitleLogo
                                className="w-full h-auto transform-gpu transition-transform duration-300"
                                style={{
                                    transform: glitchIntensity > 0.9 ? `skew(${Math.random() * 6 - 3}deg) translate(${Math.random() * 4 - 2}px, ${Math.random() * 4 - 2}px)` : 'none',
                                    filter: buttonsVisible ? 'none' : 'blur(20px) brightness(3)'
                                }}
                            />
                            
                            {/* Effet de lueur pulsante derrière le titre */}
                            {buttonsVisible && (
                                <div 
                                    className="absolute inset-0 -z-10"
                                    style={{
                                        background: 'radial-gradient(ellipse at center, rgba(6,182,212,0.3) 0%, transparent 70%)',
                                        animationName: 'glowPulse',
                                        animationDuration: '3s',
                                        animationTimingFunction: 'ease-in-out',
                                        animationIterationCount: 'infinite',
                                        animationDelay: '1.5s'
                                    }}
                                />
                            )}
                        </div>

                        {/* Lignes d'énergie qui traversent le titre */}
                        {buttonsVisible && (
                            <>
                                <div 
                                    className="absolute top-1/4 left-0 w-full h-px bg-gradient-to-r from-transparent via-cyan-400 to-transparent"
                                    style={{
                                        animation: 'energyLine 1.5s ease-out forwards',
                                        animationDelay: '0.3s',
                                        opacity: 0,
                                        boxShadow: '0 0 10px rgba(6,182,212,0.8)'
                                    }}
                                />
                                <div 
                                    className="absolute top-3/4 left-0 w-full h-px bg-gradient-to-r from-transparent via-blue-400 to-transparent"
                                    style={{
                                        animation: 'energyLine 1.5s ease-out forwards',
                                        animationDelay: '0.5s',
                                        opacity: 0,
                                        boxShadow: '0 0 10px rgba(59,130,246,0.8)'
                                    }}
                                />
                            </>
                        )}
                    </div>

                    {/* Styles CSS pour les animations */}
                    <style>{`
                        @keyframes scanReveal {
                            0% {
                                top: -10%;
                                opacity: 0;
                            }
                            20% {
                                opacity: 1;
                            }
                            100% {
                                top: 110%;
                                opacity: 0;
                            }
                        }

                        @keyframes particleConverge {
                            0% {
                                opacity: 0;
                                transform: scale(0);
                            }
                            20% {
                                opacity: 1;
                            }
                            100% {
                                opacity: 0;
                                transform: translate(
                                    calc(50vw - 50%),
                                    calc(50vh - 50%)
                                ) scale(0);
                            }
                        }

                        @keyframes gridMaterialize {
                            0% {
                                opacity: 0;
                                transform: scale(1.5);
                            }
                            50% {
                                opacity: 0.3;
                            }
                            100% {
                                opacity: 0;
                                transform: scale(1);
                            }
                        }

                        @keyframes titleGlitchIn {
                            0% {
                                opacity: 0;
                                transform: scale(0.8) translateY(20px);
                                filter: blur(20px) brightness(3) hue-rotate(180deg);
                            }
                            10% {
                                opacity: 0.3;
                                transform: scale(0.85) translateY(15px) skewX(-5deg);
                                filter: blur(15px) brightness(2.5) hue-rotate(90deg);
                            }
                            20% {
                                opacity: 0.5;
                                transform: scale(0.9) translateY(10px) skewX(5deg);
                                filter: blur(10px) brightness(2) hue-rotate(-90deg);
                            }
                            30% {
                                opacity: 0.7;
                                transform: scale(0.95) translateY(5px) skewX(-3deg);
                                filter: blur(5px) brightness(1.5);
                            }
                            40% {
                                opacity: 0.8;
                                transform: scale(1.02) translateY(0) skewX(2deg);
                                filter: blur(3px) brightness(1.3);
                            }
                            50% {
                                opacity: 0.9;
                                transform: scale(1.05) skewX(-1deg);
                                filter: blur(2px) brightness(1.2);
                            }
                            70% {
                                opacity: 1;
                                transform: scale(1.02) skewX(0.5deg);
                                filter: blur(1px) brightness(1.1);
                            }
                            100% {
                                opacity: 1;
                                transform: scale(1) translateY(0) skewX(0);
                                filter: blur(0) brightness(1);
                            }
                        }

                        @keyframes energyLine {
                            0% {
                                opacity: 0;
                                transform: scaleX(0);
                            }
                            50% {
                                opacity: 1;
                                transform: scaleX(1);
                            }
                            100% {
                                opacity: 0;
                                transform: scaleX(1);
                            }
                        }

                        @keyframes glowPulse {
                            0%, 100% {
                                opacity: 0.3;
                                transform: scale(1);
                            }
                            50% {
                                opacity: 0.6;
                                transform: scale(1.1);
                            }
                        }
                    `}</style>

                    <div className={`w-full border border-slate-600 p-8 shadow-2xl shadow-black/50 bg-slate-900/95 relative z-10 ${
                        isTransitioning ? 'opacity-0 scale-95 translate-y-4' : buttonsVisible ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-95 translate-y-8'
                    }`}
                        onClick={handleUserInteraction}
                        style={{
                            transitionProperty: 'all',
                            transitionDuration: '1000ms',
                            transitionTimingFunction: 'ease',
                            transitionDelay: buttonsVisible && !isTransitioning ? '400ms' : '0ms',
                            transform: glitchIntensity > 0.7 ? `skew(${(Math.sin(glitchTick * 0.5) * 2)}deg, ${(Math.cos(glitchTick * 0.7) * 1.5)}deg) translate(${Math.sin(glitchTick * 0.3) * 3}px, ${Math.cos(glitchTick * 0.4) * 3}px)` : 'none',
                            filter: glitchIntensity > 0 ? `
                                brightness(${1 + glitchIntensity * 0.2})
                                contrast(${1 + glitchIntensity * 0.3})
                                saturate(${1 + glitchIntensity * 0.5})
                                hue-rotate(${Math.sin(glitchTick * 0.2) * 30 * glitchIntensity}deg)
                            ` : 'none',
                            boxShadow: glitchIntensity > 0 ? `
                                0 0 ${20 * glitchIntensity}px rgba(6,182,212,${0.4 * glitchIntensity}),
                                0 0 ${40 * glitchIntensity}px rgba(239,68,68,${0.2 * glitchIntensity})
                            ` : undefined
                        }}
                    >
                        {/* Effet de corruption RGB split - Plus subtil */}
                        {glitchIntensity > 0.5 && (
                            <>
                                <div 
                                    className="absolute inset-0 bg-red-500 mix-blend-screen pointer-events-none"
                                    style={{
                                        opacity: 0.15 * glitchIntensity,
                                        transform: `translate(${Math.sin(glitchTick * 0.5) * 4 * glitchIntensity}px, 0)`
                                    }}
                                />
                                <div 
                                    className="absolute inset-0 bg-cyan-500 mix-blend-screen pointer-events-none"
                                    style={{
                                        opacity: 0.15 * glitchIntensity,
                                        transform: `translate(${Math.sin(glitchTick * 0.7) * -4 * glitchIntensity}px, 0)`
                                    }}
                                />
                            </>
                        )}

                        {/* Lignes de corruption horizontales - Réduites */}
                        {glitchIntensity > 0.6 && (
                            <>
                                {[...Array(3)].map((_, i) => (
                                    <div
                                        key={`glitch-line-${i}`}
                                        className="absolute left-0 right-0 bg-gradient-to-r from-transparent via-cyan-400 to-transparent pointer-events-none"
                                        style={{
                                            top: `${(i * 30 + Math.sin(glitchTick * 0.3 + i) * 10) % 100}%`,
                                            height: `2px`,
                                            opacity: 0.3 * glitchIntensity,
                                            transform: `translateX(${Math.sin(glitchTick * 0.4 + i) * 50 * glitchIntensity}px)`,
                                            mixBlendMode: 'overlay'
                                        }}
                                    />
                                ))}
                            </>
                        )}

                        {/* Blocs de pixels corrompus - Réduits */}
                        {glitchIntensity > 0.7 && (
                            <>
                                {[...Array(5)].map((_, i) => (
                                    <div
                                        key={`corrupt-block-${i}`}
                                        className="absolute bg-cyan-500/30 pointer-events-none"
                                        style={{
                                            left: `${(Math.sin(glitchTick * 0.2 + i) * 50 + 50) % 100}%`,
                                            top: `${(Math.cos(glitchTick * 0.3 + i) * 50 + 50) % 100}%`,
                                            width: `${30 + Math.random() * 40}px`,
                                            height: `${15 + Math.random() * 20}px`,
                                            opacity: 0.2 * glitchIntensity,
                                            mixBlendMode: 'screen'
                                        }}
                                    />
                                ))}
                            </>
                        )}

                        {/* Effet de scan vertical */}
                        {glitchIntensity > 0.8 && (
                            <div
                                className="absolute inset-0 pointer-events-none"
                                style={{
                                    background: `linear-gradient(
                                        to bottom,
                                        transparent ${Math.sin(glitchTick * 0.8) * 50 + 50}%,
                                        rgba(6,182,212,0.2) ${Math.sin(glitchTick * 0.8) * 50 + 50.5}%,
                                        transparent ${Math.sin(glitchTick * 0.8) * 50 + 51}%
                                    )`,
                                    opacity: 0.5 * glitchIntensity
                                }}
                            />
                        )}

                        {/* Contenu de la fenêtre */}
                        <div className="text-center mb-8 border-b border-slate-600 pb-6"
                            style={{
                                textShadow: glitchIntensity > 0.6 ? `
                                    ${Math.sin(glitchTick * 0.3) * 2 * glitchIntensity}px 0 rgba(255,0,0,${0.3 * glitchIntensity}),
                                    ${Math.sin(glitchTick * 0.5) * -2 * glitchIntensity}px 0 rgba(0,255,255,${0.3 * glitchIntensity})
                                ` : 'none'
                            }}
                        >
                            {/* Logo/Marque ALBION OS ultra stylisé */}
                            <div className="mb-2 relative py-4">


                                {/* Titre principal ALBION OS */}
                                <div className="relative text-center inline-block mx-auto">
                                    {/* Cadre hexagonal décoratif - Maintenant centré sur le titre */}
                                    <div className="absolute inset-0 flex items-center justify-center opacity-20 pointer-events-none scale-125 -translate-y-4">
                                        <svg width="320" height="120" viewBox="0 0 320 120">
                                            <polygon
                                                points="160,10 240,40 240,80 160,110 80,80 80,40"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="1"
                                                className="text-cyan-500"
                                            />
                                            <polygon
                                                points="160,15 235,42 235,78 160,105 85,78 85,42"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="0.5"
                                                className="text-cyan-400 animate-pulse"
                                            />
                                        </svg>
                                    </div>

                                    {/* Effet de grille derrière */}
                                    {/* Effet de grille derrière */}
                                    <div className="absolute inset-0 opacity-10 pointer-events-none">
                                        <div className="absolute inset-0" style={{
                                            backgroundImage: 'linear-gradient(0deg, transparent 24%, rgba(34, 211, 238, 0.3) 25%, rgba(34, 211, 238, 0.3) 26%, transparent 27%, transparent 74%, rgba(34, 211, 238, 0.3) 75%, rgba(34, 211, 238, 0.3) 76%, transparent 77%, transparent), linear-gradient(90deg, transparent 24%, rgba(34, 211, 238, 0.3) 25%, rgba(34, 211, 238, 0.3) 26%, transparent 27%, transparent 74%, rgba(34, 211, 238, 0.3) 75%, rgba(34, 211, 238, 0.3) 76%, transparent 77%, transparent)',
                                            backgroundSize: '8px 8px'
                                        }}></div>
                                    </div>



                                    {/* Lignes de soulignement - positionnées sur le conteneur parent */}
                                    <span className="absolute -bottom-2 left-0 right-0 h-[3px] bg-gradient-to-r from-transparent via-cyan-400 to-transparent shadow-[0_0_8px_rgba(34,211,238,0.6)] pointer-events-none"></span>
                                    <span className="absolute -bottom-3 left-8 right-8 h-[1px] bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent pointer-events-none"></span>

                                    <h1 className="text-4xl font-black text-cyan-400 uppercase relative py-2" style={{ letterSpacing: '0.5em', marginRight: '-0.5em' }}>
                                        {/* Texte principal avec effet de profondeur */}
                                        <span className="relative z-20 inline-block" style={{
                                            textShadow: '0 0 10px rgba(34, 211, 238, 0.8), 0 0 20px rgba(34, 211, 238, 0.5), 0 0 30px rgba(34, 211, 238, 0.3), 2px 2px 0px rgba(0, 0, 0, 0.5)'
                                        }}>
                                            ALBION OS
                                        </span>

                                        {/* Couches de lueur multiples */}
                                        <span className="absolute inset-0 text-cyan-300 blur-md opacity-60 z-10 animate-pulse">ALBION OS</span>
                                        <span className="absolute inset-0 text-cyan-500 blur-lg opacity-40 z-0">ALBION OS</span>
                                        <span className="absolute inset-0 text-blue-400 blur-xl opacity-20 z-0 animate-pulse" style={{ animationDelay: '0.5s' }}>ALBION OS</span>
                                    </h1>
                                </div>

                                {/* Sous-titre technique */}
                                <div className="mt-4 space-y-1">

                                    <p className="text-[8px] text-slate-400 tracking-widest font-mono">{t('loginScreen.version')}</p>
                                    <div className="flex items-center justify-center gap-2 text-[8px] text-slate-500 mt-2">
                                        <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse shadow-[0_0_4px_rgba(34,197,94,0.8)]"></span>
                                        <span className="tracking-wider">SYSTÈME OPÉRATIONNEL</span>
                                        <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse shadow-[0_0_4px_rgba(34,197,94,0.8)]"></span>
                                    </div>
                                </div>
                            </div>

                            {/* Badge d'accès dynamique */}
                            <div className="relative inline-block transition-all duration-300">
                                {loginStatus === 'IDLE' && (
                                    <p className="text-[10px] font-bold tracking-[0.25em] text-red-400 uppercase border-2 border-red-600/50 bg-red-950/40 inline-block px-4 py-1.5 shadow-[0_0_10px_rgba(220,38,38,0.3)] relative">
                                        <span className="relative z-10">{t('loginScreen.restrictedAccess')}</span>
                                        <span className="absolute inset-0 bg-red-500/10 animate-pulse"></span>
                                    </p>
                                )}
                                {loginStatus === 'SUCCESS' && (
                                    <p className="text-[10px] font-bold tracking-[0.25em] text-emerald-400 uppercase border-2 border-emerald-500/50 bg-emerald-950/60 inline-block px-4 py-1.5 shadow-[0_0_20px_rgba(16,185,129,0.5)] relative animate-pulse">
                                        <span className="relative z-10 flex items-center gap-2">
                                            <span>ACCESS GRANTED</span>
                                        </span>
                                        <span className="absolute inset-0 bg-emerald-500/20"></span>
                                    </p>
                                )}
                                {loginStatus === 'ERROR' && (
                                    <p className="text-[10px] font-bold tracking-[0.25em] text-red-500 uppercase border-2 border-red-500 bg-red-950/80 inline-block px-4 py-1.5 shadow-[0_0_20px_rgba(239,68,68,0.6)] relative animate-shake">
                                        <span className="relative z-10">ACCESS DENIED</span>
                                        <span className="absolute inset-0 bg-red-500/20 animate-pulse-fast"></span>
                                    </p>
                                )}

                                {/* Coins du badge (couleur dynamique) */}
                                <span className={`absolute -top-0.5 -left-0.5 w-2 h-2 border-t border-l transition-colors duration-300 ${loginStatus === 'SUCCESS' ? 'border-emerald-500' : loginStatus === 'ERROR' ? 'border-red-500' : 'border-red-500'}`}></span>
                                <span className={`absolute -top-0.5 -right-0.5 w-2 h-2 border-t border-r transition-colors duration-300 ${loginStatus === 'SUCCESS' ? 'border-emerald-500' : loginStatus === 'ERROR' ? 'border-red-500' : 'border-red-500'}`}></span>
                                <span className={`absolute -bottom-0.5 -left-0.5 w-2 h-2 border-b border-l transition-colors duration-300 ${loginStatus === 'SUCCESS' ? 'border-emerald-500' : loginStatus === 'ERROR' ? 'border-red-500' : 'border-red-500'}`}></span>
                                <span className={`absolute -bottom-0.5 -right-0.5 w-2 h-2 border-b border-r transition-colors duration-300 ${loginStatus === 'SUCCESS' ? 'border-emerald-500' : loginStatus === 'ERROR' ? 'border-red-500' : 'border-red-500'}`}></span>
                            </div>

                            {/* Indicateur si interaction requise pour la musique */}
                            {needsUserInteraction && (
                                <p className="text-[8px] text-yellow-500 mt-3 animate-pulse">
                                    {t('loginScreen.clickToActivateAudio')}
                                </p>
                            )}
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-6" autoComplete="off">
                            <div>
                                <label className="block text-xs mb-2 text-slate-400">{t('login.username')}</label>
                                <input
                                    autoFocus
                                    type="text"
                                    value={username}
                                    onChange={(e) => {
                                        setUsername(e.target.value.toUpperCase());
                                        if (loginStatus === 'ERROR') setLoginStatus('IDLE');
                                        if (error) setError('');
                                    }}
                                    className={`w-full bg-slate-800 border p-2 text-cyan-300 outline-none focus:border-cyan-500 transition-colors ${loginStatus === 'ERROR' ? 'border-red-500 animate-shake' : 'border-slate-600'}`}
                                    autoComplete="off"
                                    autoCorrect="off"
                                    autoCapitalize="off"
                                    spellCheck="false"
                                />
                            </div>
                            <div>
                                <label className="block text-xs mb-2 text-slate-400">{t('login.password')}</label>
                                <input
                                    type="password"
                                    value={password}
                                    onChange={(e) => {
                                        setPassword(e.target.value);
                                        if (loginStatus === 'ERROR') setLoginStatus('IDLE');
                                        if (error) setError('');
                                    }}
                                    className={`w-full bg-slate-800 border p-2 text-cyan-300 outline-none focus:border-cyan-500 transition-colors ${loginStatus === 'ERROR' ? 'border-red-500 animate-shake' : 'border-slate-600'}`}
                                    autoComplete="off"
                                    autoCorrect="off"
                                    autoCapitalize="off"
                                    spellCheck="false"
                                />
                            </div>
                            {error && <div className="text-[10px] text-red-400 font-bold bg-red-900/20 p-2 border border-red-800/50">{error}</div>}
                            <button
                                type="submit"
                                className="w-full bg-slate-700 text-cyan-400 font-bold py-2 hover:bg-slate-600 transition-colors border border-slate-600"
                            >
                                {t('loginScreen.authenticate')}
                            </button>

                            {/* Bouton de login automatique pour le développement */}
                            {false && process.env.NODE_ENV === 'development' && savedUser && (
                                <button
                                    type="button"
                                    onClick={async () => {
                                        if (!savedUser) return;
                                        setUsername(savedUser.u);
                                        setPassword(savedUser.p);
                                        // Trigger Success Visuals directly
                                        setLoginStatus('SUCCESS');
                                        playSuccessSound(); // Son de connexion réussie

                                        setTimeout(async () => {
                                            setIsTransitioning(true);
                                            console.log('LoginScreen - Auto-login successful, emitting USER_SESSION_CHANGED');
                                            window.dispatchEvent(new CustomEvent('USER_SESSION_CHANGED'));
                                            await fadeOutMusic();
                                            setStep('POST_LOGIN_BOOT');
                                        }, 1500);
                                    }}
                                    className="w-full bg-amber-700 text-amber-200 font-bold py-2 hover:bg-amber-600 transition-colors border border-amber-600 text-xs"
                                    title={t('loginScreen.autoLoginDev')}
                                >
                                    🚀 {t('loginScreen.autoLoginButton')}
                                </button>
                            )}
                        </form>

                        <div className="mt-6 text-center">
                            <p className="text-[8px] text-slate-500">{t('loginScreen.dstProperty')}</p>
                        </div>
                    </div>
                </div>
            ) : null}
            {gameSettings.crtEffects && (
                <div className="fixed inset-0 pointer-events-none crt-scanlines opacity-20"></div>
            )}

            {/* Modal des paramètres du jeu */}
            <GameSettings
                isOpen={isSettingsOpen}
                onClose={() => setIsSettingsOpen(false)}
            />
        </div>
    );
};
